import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { X, Zap, AlertTriangle, Search } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SimulatedBadge from "@/components/paper/SimulatedBadge";
import ExpiryBadge from "@/components/paper/ExpiryBadge";
import StrategyRefChip from "@/components/strategy/StrategyRefChip";
import StrategyStatusChip from "@/components/strategy/StrategyStatusChip";
import { strategyRef, matchesStrategyQuery, noStrategyMatchText, STRATEGY_SEARCH_PLACEHOLDER } from "@/lib/strategyRef";
import useIsNarrow from "@/hooks/useIsNarrow";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle,
  AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const PIPELINE_COLORS = {
  momentum:        "bg-blue-500/15 text-blue-400",
  orb:             "bg-blue-500/15 text-blue-400",
  breakout:        "bg-blue-500/15 text-blue-400",
  commodity_gap:   "bg-orange-500/15 text-orange-400",
  vwap:            "bg-orange-500/15 text-orange-400",
  mean_reversion:  "bg-orange-500/15 text-orange-400",
  fvg_ob:          "bg-purple-500/15 text-purple-400",
  liquidity_sweep: "bg-purple-500/15 text-purple-400",
  trap:            "bg-emerald-500/15 text-emerald-400",
};

const PIPELINE_LABELS = {
  momentum:        "Momentum",
  orb:             "ORB",
  breakout:        "Breakout",
  commodity_gap:   "Commodity Gap",
  vwap:            "VWAP",
  mean_reversion:  "Mean Rev",
  fvg_ob:          "FVG/OB",
  liquidity_sweep: "Liq Sweep",
  trap:            "Trap",
};

function SourceBadge({ pipeline }) {
  const color = PIPELINE_COLORS[pipeline] || "bg-zinc-500/15 text-zinc-400";
  const label = PIPELINE_LABELS[pipeline] || pipeline;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-section tracking-wide ${color}`}>
      {label}
    </span>
  );
}

const fmt = (n, d = 2) =>
  (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const pnlClass = (v) => (v > 0 ? "txt-profit" : v < 0 ? "txt-loss" : "txt-muted");

// Type column: index and MCX symbols are futures even when a legacy row
// stored "EQ" (the backend sends the normalised display_type); EQ only for
// cash equity (RU-PNL G-37).
export function positionTypeLabel(p) {
  if (p.instrument_type === "OPT") return `${p.option_strike} ${p.option_kind}`;
  const t = p.display_type || p.instrument_type || "EQ";
  if (t === "FUT") return p.exchange === "MCX" ? "MCX FUT" : "FUT";
  return t;
}

// Cell contents shared by the desktop row and the phone card (RU-MOBILECARDS MC-2).
// RU-EXITSAFE XS-6: an option whose stop/target are underlying prices (level_basis "underlying", RU-G35) is
// compared with the underlying's LTP by its view (bullish: stop below, target above; bearish mirrored), never
// with the option premium; no underlying price, no flag.
export const posFlags = (p) => {
  if (p.level_basis === "underlying") {
    const u = p.underlying_ltp;
    const bull = p.view === "BULLISH";
    const bear = p.view === "BEARISH";
    if (!u || (!bull && !bear)) return { slHit: false, tgtHit: false };
    return {
      slHit: !!p.stop_loss && (bull ? u <= p.stop_loss : u >= p.stop_loss),
      tgtHit: !!p.target_price && (bull ? u >= p.target_price : u <= p.target_price),
    };
  }
  return {
    slHit: p.stop_loss && p.ltp && (p.qty > 0 ? p.ltp <= p.stop_loss : p.ltp >= p.stop_loss),
    tgtHit: p.target_price && p.ltp && (p.qty > 0 ? p.ltp >= p.target_price : p.ltp <= p.target_price),
  };
};

function SymbolContent({ p, slHit, tgtHit }) {
  return (
    <>
      {slHit && <AlertTriangle className="w-3 h-3 txt-loss" title="SL breached" />}
      {tgtHit && <Zap className="w-3 h-3 txt-profit" title="Target hit" />}
      {p.symbol}
      {p.simulated ? (
        <SimulatedBadge testid={`position-simulated-${p.id}`} />
      ) : p.mark_simulated ? (
        <SimulatedBadge testid={`position-simulated-${p.id}`}
                        title="Marked on a generated option chain, not live market data" />
      ) : null}
    </>
  );
}

const typeTitle = (p) => (p.legacy_instrument_type ? `Stored as ${p.legacy_instrument_type}; shown as the futures contract it is` : undefined);

// RU-STRATLINK SL-4/SL-5/SL-12: the strategy's short reference (the shared helper and chip) and a link to it.
export { strategyRef };
export const strategyHref = (id) => `/strategies?strategy=${encodeURIComponent(id)}`;

// An in-app navigation through the router's browser history (this component is also rendered outside a router,
// so it does not import react-router): push the URL, then the popstate the router listens to. A modified or
// non-primary click keeps the browser's own behaviour (new tab / window).
export function openInApp(e, href) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  const idx = window.history.state?.idx;
  window.history.pushState({ usr: null, key: Math.random().toString(36).slice(2, 10),
                             idx: typeof idx === "number" ? idx + 1 : 0 }, "", href);
  window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
}

function StrategyContent({ p, enabled }) {
  if (!p.strategy_id) {
    return p.strategy_name ? <span className="font-section">{p.strategy_name}</span> : <span className="txt-muted">Manual</span>;
  }
  const href = strategyHref(p.strategy_id);
  const name = p.strategy_name || "Strategy";
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <a href={href} onClick={(e) => openInApp(e, href)} className="font-section underline-offset-2 hover:underline"
         data-testid={`position-strategy-link-${p.id}`} title="Open in Strategy Builder">{name}</a>
      <StrategyRefChip id={p.strategy_id} testid={`position-strategy-ref-${p.id}`} />
      {/* RU-STRATSTATUS SS-5: this row is an open paper position, so a paused paper strategy reads EXITS ONLY */}
      <StrategyStatusChip src={{ ...p, strategy_enabled: enabled }} openPosition testid={`position-strategy-status-${p.id}`} />
    </span>
  );
}

// RU-STRATLINK SL-6: pause / resume PAPER trading of the position's strategy; live is never touched from here.
function StrategyPauseControl({ p, enabled, busy, onAsk }) {
  if (!p.strategy_id) return null;
  if (p.strategy_mode === "live") {
    return <div className="text-[10px] txt-muted" data-testid={`position-strategy-live-${p.id}`}>live — manage in Strategy Builder</div>;
  }
  if (p.strategy_mode !== "paper" || !p.strategy_pausable) return null;
  const btn = "rounded-none h-6 px-2 text-[10px] font-section tracking-wider";
  return enabled === false ? (
    <div className="flex items-center gap-1 mt-0.5">
      <span className="text-[10px] font-section px-1 border border-amber-500/40 text-amber-400" data-testid={`position-strategy-paused-${p.id}`}>Paused</span>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => onAsk(p, true)} className={btn}
              data-testid={`position-strategy-resume-${p.id}`}>Resume</Button>
    </div>
  ) : enabled === true ? (
    <div className="mt-0.5">
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => onAsk(p, false)} className={`${btn} hover:text-amber-400`}
              data-testid={`position-strategy-pause-${p.id}`}>Pause strategy</Button>
    </div>
  ) : null;
}

const SourceContent = ({ p }) => (p.source_pipeline ? (
  <SourceBadge pipeline={p.source_pipeline} />
) : (
  <span className="txt-muted">—</span>
));

function LotContent({ p }) {
  return p.lot_unknown ? (
    <div className="text-[10px] txt-muted whitespace-nowrap" data-testid={`position-per-unit-${p.id}`}
         title="Lot size unknown: P&L is per unit, not per lot">per unit</div>
  ) : p.lot_size > 1 ? (
    <div className="text-[10px] txt-muted whitespace-nowrap"
         title={p.lot_source === "stored" ? "Lot size stored when the position opened" : "Lot size from the exchange table"}>
      × {p.lot_size}
    </div>
  ) : null;
}

const AvgContent = ({ p }) => (p.bad_avg_price ? (
  <span className="inline-flex items-center gap-1" data-testid={`position-bad-avg-${p.id}`}
        title="Filled at price 0: P&L not meaningful, excluded from total P&L">
    <AlertTriangle className="w-3 h-3" />{fmt(p.avg_price)}
  </span>
) : fmt(p.avg_price));

// RU-PAPERFILL2 PF2-5b: the RU-PAPERFILL marks. No fresh quote: the last real mark (stale, with its age);
// no real mark at all: unmarked (no LTP / P&L, left out of the total). Never the entry price.
const markAge = (s) => (s == null ? "" : s < 90 ? ` (${Math.round(s)}s old)` : s < 5400 ? ` (${Math.round(s / 60)} min old)`
  : ` (${Math.round(s / 3600)} h old)`);
export const ltpTitle = (p) => (p.unmarked
  ? "No real quote for this contract yet: not marked, P&L unknown and left out of the total"
  : p.ltp_stale ? `No fresh quote: shown at the last real mark${markAge(p.mark_age_s)}; the P&L is an estimate`
  : undefined);

const LtpContent = ({ p }) => (
  <>
    {fmt(p.ltp)}
    {p.ltp_stale ? <div className="text-[10px] txt-muted" data-testid={`position-stale-${p.id}`}>stale</div> : null}
  </>
);

const slTitle = (p) => (p.managed_by === "runner" ? `Enforced by the strategy runner${p.trailing_pct ? ` · trailing ${p.trailing_pct}%` : ""}${p.default_stop ? " · default stop" : ""}` : undefined);

const SlContent = ({ p }) => (
  <>
    {p.stop_loss ? fmt(p.stop_loss) : "—"}
    {p.stop_loss && p.trailing_pct ? <span className="ml-1 txt-secondary">T</span> : null}
    {p.stop_loss && p.default_stop ? <span className="ml-1 txt-secondary">d</span> : null}
  </>
);

const tgtTitle = (p) => (p.target_2 ? `T2 ${fmt(p.target_2)}${p.t1_done ? " · T1 done" : ""}` : undefined);

const TargetContent = ({ p }) => (
  <>
    {p.target_price ? fmt(p.target_price) : "—"}
    {p.t1_done ? <span className="ml-1 txt-profit">✓</span> : null}
    {p.target_2 ? <span className="ml-1 txt-secondary">/{fmt(p.target_2)}</span> : null}
  </>
);

/* Computed by the backend on the same lot as the P&L (RU-PNL G-36). */
const pnlPctText = (p) => (p.pnl_pct != null ? `${Number(p.pnl_pct).toFixed(2)}%` : "—");

const entryText = (p) => (p.created_at
  ? new Date(p.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })
  : "—");

function CardField({ label, children, className = "", title, testid }) {
  return (
    <div className="min-w-0" title={title} data-testid={testid}>
      <div className="overline text-[10px] txt-muted">{label}</div>
      <div className={`font-mono-data text-xs break-words ${className}`}>{children}</div>
    </div>
  );
}

// RU-REFSEARCH RS-2: a position matches by its symbol, its strategy's name, or its strategy ref / id
const POSITION_FIELDS = { name: (p) => p.strategy_name, symbol: (p) => p.symbol, id: (p) => p.strategy_id };

export default function PositionsTable({ positions, onFlatten, onRefresh }) {
  const [search, setSearch] = useState("");
  // display only (RS-4): the count, PANIC EXIT ALL and the pause dialog keep acting on every position
  const shown = useMemo(() => positions.filter((p) => matchesStrategyQuery(p, search, POSITION_FIELDS)), [positions, search]);
  const noMatch = positions.length > 0 && shown.length === 0;
  const [closingId, setClosingId] = useState(null);
  const [panicAll, setPanicAll] = useState(false);
  const narrow = useIsNarrow();
  const [confirmOpen, setConfirmOpen] = useState(false);   // RU-MOBILECARDS MC-9
  // RU-STRATLINK SL-6: {p, enable} awaiting confirmation; per-strategy enabled state set by a pause / resume
  // until the next positions refresh; the strategy id whose call is in flight.
  const [pauseAsk, setPauseAsk] = useState(null);
  const [enabledOverride, setEnabledOverride] = useState({});
  const [pausingSid, setPausingSid] = useState(null);
  useEffect(() => { setEnabledOverride({}); }, [positions]);
  const strategyEnabled = (p) => (p.strategy_id in enabledOverride ? enabledOverride[p.strategy_id] : p.strategy_enabled);

  const setStrategyPaper = async (p, enable) => {
    if (!p.strategy_id || p.strategy_mode !== "paper" || !p.strategy_pausable) {   // never a live strategy
      toast.error("Not a paper strategy that can be paused here: manage it in Strategy Builder");
      return;
    }
    const sid = p.strategy_id;
    setPausingSid(sid);
    try {
      // SL-11: the server refuses (409) when the stored deployment is not paper any more
      await api.post(`/strategies/${sid}/deploy`, { mode: "paper", enabled: enable, expect_mode: "paper" });
      setEnabledOverride((o) => ({ ...o, [sid]: enable }));
      toast.success(enable ? `Paper trading resumed for ${p.strategy_name || strategyRef(sid)}`
                           : `Paper trading paused for ${p.strategy_name || strategyRef(sid)}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (enable ? "Resume failed" : "Pause failed"));
      if (e?.response?.status === 409) onRefresh?.();   // SL-11: show the strategy's current mode
    } finally {
      setPausingSid(null);
    }
  };

  const strategyCell = (p) => (
    <>
      <StrategyContent p={p} enabled={strategyEnabled(p)} />
      <StrategyPauseControl p={p} enabled={strategyEnabled(p)} busy={pausingSid === p.strategy_id}
                            onAsk={(pos, enable) => setPauseAsk({ p: pos, enable })} />
    </>
  );

  const closeOne = async (pos) => {
    setClosingId(pos.id);
    try {
      await api.post(`/paper/positions/${pos.id}/close`);
      toast.success(`Closed ${pos.symbol}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    } finally {
      setClosingId(null);
    }
  };

  const handleFlatten = async () => {
    setPanicAll(true);
    try {
      await onFlatten?.();
    } finally {
      setPanicAll(false);
    }
  };

  const closeButton = (p) => (
    <Button
      size="sm"
      variant="ghost"
      disabled={closingId === p.id}
      onClick={() => closeOne(p)}
      className="rounded-none h-7 w-7 p-0 text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
      title="Panic exit this position"
      data-testid={`position-close-${p.id}`}
    >
      {closingId === p.id ? "…" : <X className="w-3.5 h-3.5" />}
    </Button>
  );

  return (
    <div className="panel p-3 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="overline">Open positions</div>
          {positions.length > 0 && (
            <span className="text-xs font-mono-data txt-muted">{positions.length} open</span>
          )}
          {positions.length > 0 && (
            <label className="flex items-center gap-1.5 border border-[var(--border)] px-2 py-1">
              <Search className="w-3.5 h-3.5 txt-secondary" />
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={STRATEGY_SEARCH_PLACEHOLDER} aria-label="Search positions"
                data-testid="positions-search"
                className="bg-transparent text-xs outline-none w-40 sm:w-52" />
            </label>
          )}
        </div>
        {positions.length > 0 && (
          <Button
            data-testid="paper-flatten-btn"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={panicAll}
            className="rounded-none bg-red-600 hover:bg-red-500 text-white font-section tracking-wider text-xs h-8 px-3"
          >
            <Zap className="w-3.5 h-3.5 mr-1" />
            {panicAll ? "CLOSING…" : "PANIC EXIT ALL"}
          </Button>
        )}
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!o) setConfirmOpen(false); }}>
        <AlertDialogContent data-testid="paper-flatten-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Close all {positions.length} paper positions now?</AlertDialogTitle>
            <AlertDialogDescription>
              Closes each open paper position at market and books its P&L. Positions blocked by the kill switch or without a price stay open. Live broker positions are not touched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="paper-flatten-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="paper-flatten-confirm"
              className="bg-red-600 hover:bg-red-500 text-white"
              onClick={() => { setConfirmOpen(false); handleFlatten(); }}
            >
              Exit all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!pauseAsk} onOpenChange={(o) => { if (!o) setPauseAsk(null); }}>
        <AlertDialogContent data-testid="strategy-pause-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pauseAsk?.enable
                ? `Resume paper trading for ${pauseAsk?.p.strategy_name || strategyRef(pauseAsk?.p.strategy_id)}?`
                : `Pause paper trading for ${pauseAsk?.p.strategy_name || strategyRef(pauseAsk?.p.strategy_id)}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pauseAsk?.enable
                ? "It can open new paper positions."
                : "Open positions are not closed; exits keep running."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="strategy-pause-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="strategy-pause-confirm"
              onClick={() => {
                const a = pauseAsk;
                setPauseAsk(null);
                // the row as it is now: a refresh while the dialog was open may show the strategy live, or the
                // row may be gone (position closed): then its current mode is unknown and nothing is sent
                if (!a) return;
                const cur = positions.find((x) => x.id === a.p.id);
                if (!cur) {
                  toast.error("The position is no longer listed: open the strategy in Strategy Builder to pause it");
                  return;
                }
                setStrategyPaper(cur, a.enable);
              }}
            >
              {pauseAsk?.enable ? "Resume" : "Pause"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {narrow ? (
        <div className="space-y-2" data-testid="positions-cards">
          {positions.length === 0 && (
            <div className="text-center txt-muted py-6 text-sm">No open positions.</div>
          )}
          {noMatch && (
            <div className="text-center txt-muted py-6 text-sm" data-testid="positions-search-empty">{noStrategyMatchText(search)}</div>
          )}
          {shown.map((p) => {
            const { slHit, tgtHit } = posFlags(p);
            return (
              <div
                key={p.id}
                className={`border border-[var(--border)] p-3 ${slHit ? "bg-red-500/5" : tgtHit ? "bg-emerald-500/5" : ""}`}
                data-testid={`position-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1.5 min-w-0 font-section break-words">
                    <SymbolContent p={p} slHit={slHit} tgtHit={tgtHit} />
                    <span className="font-mono-data text-[10px] px-1.5 py-0.5 border border-[var(--border)] txt-secondary"
                          title={typeTitle(p)} data-testid={`position-card-type-${p.id}`}>
                      {positionTypeLabel(p)}
                    </span>
                  </div>
                  {closeButton(p)}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-2">
                  <CardField label="Qty × lot" className={p.qty > 0 ? "txt-profit" : "txt-loss"} testid={`position-card-qty-${p.id}`}>
                    {p.qty}
                    <LotContent p={p} />
                  </CardField>
                  <CardField label="Avg" className={p.bad_avg_price ? "txt-loss" : ""} testid={`position-card-avg-${p.id}`}>
                    <AvgContent p={p} />
                  </CardField>
                  <CardField label="LTP" className={p.ltp_stale ? "txt-muted" : ""} title={ltpTitle(p)} testid={`position-card-ltp-${p.id}`}>
                    <LtpContent p={p} />
                  </CardField>
                  <CardField label="P&L" className={pnlClass(p.pnl)} testid={`position-card-pnl-${p.id}`}>
                    {fmt(p.pnl)} <span className="text-[10px]">({pnlPctText(p)})</span>
                  </CardField>
                  <CardField label="SL" className={slHit ? "txt-loss font-bold" : "txt-muted"} title={slTitle(p)} testid={`position-card-sl-${p.id}`}>
                    <SlContent p={p} />
                  </CardField>
                  <CardField label="Target" className={tgtHit ? "txt-profit font-bold" : "txt-muted"} title={tgtTitle(p)} testid={`position-card-target-${p.id}`}>
                    <TargetContent p={p} />
                  </CardField>
                  <CardField label="Strategy" className="txt-secondary" testid={`position-card-strategy-${p.id}`}>
                    {strategyCell(p)}
                  </CardField>
                  <CardField label="Source" testid={`position-card-source-${p.id}`}>
                    <SourceContent p={p} />
                  </CardField>
                  <CardField label="Entry" className="txt-muted" title={p.created_at || ""} testid={`position-card-entry-${p.id}`}>
                    {entryText(p)}
                  </CardField>
                  {p.expiry_info ? (
                    <CardField label="Expiry" testid={`position-card-expiry-${p.id}`}>
                      <ExpiryBadge info={p.expiry_info} testid={`position-expiry-${p.id}`} />
                    </CardField>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div className="overflow-x-auto -mx-3 sm:-mx-5 px-3 sm:px-5">
      <Table>
        <TableHeader>
          <TableRow className="border-[var(--border)]">
            <TableHead className="overline">Symbol</TableHead>
            <TableHead className="overline">Type</TableHead>
            <TableHead className="overline">Strategy</TableHead>
            <TableHead className="overline">Source</TableHead>
            <TableHead className="overline text-right">Qty</TableHead>
            <TableHead className="overline text-right">Avg</TableHead>
            <TableHead className="overline text-right">LTP</TableHead>
            <TableHead className="overline text-right">SL</TableHead>
            <TableHead className="overline text-right">Target</TableHead>
            <TableHead className="overline text-right">P&L</TableHead>
            <TableHead className="overline text-right">P&L %</TableHead>
            <TableHead className="overline">Entry</TableHead>
            <TableHead className="overline"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {positions.length === 0 && (
            <TableRow className="border-[var(--border)]">
              <TableCell colSpan={13} className="text-center txt-muted py-6">No open positions.</TableCell>
            </TableRow>
          )}
          {noMatch && (
            <TableRow className="border-[var(--border)]">
              <TableCell colSpan={13} className="text-center txt-muted py-6" data-testid="positions-search-empty">
                {noStrategyMatchText(search)}
              </TableCell>
            </TableRow>
          )}
          {shown.map((p) => {
            const { slHit, tgtHit } = posFlags(p);
            return (
              <TableRow
                key={p.id}
                className={`border-[var(--border)] ${slHit ? "bg-red-500/5" : tgtHit ? "bg-emerald-500/5" : ""}`}
                data-testid={`position-${p.id}`}
              >
                <TableCell className="font-section">
                  <div className="flex items-center gap-1.5">
                    <SymbolContent p={p} slHit={slHit} tgtHit={tgtHit} />
                  </div>
                </TableCell>
                <TableCell className="font-mono-data text-xs txt-secondary"
                           title={typeTitle(p)}>
                  {positionTypeLabel(p)}
                  {p.expiry_info ? (
                    <div><ExpiryBadge info={p.expiry_info} testid={`position-expiry-${p.id}`} /></div>
                  ) : null}
                </TableCell>
                <TableCell className="text-xs txt-secondary" data-testid={`position-strategy-${p.id}`}>
                  {strategyCell(p)}
                </TableCell>
                <TableCell className="text-xs">
                  <SourceContent p={p} />
                </TableCell>
                <TableCell className={`font-mono-data text-right ${p.qty > 0 ? "txt-profit" : "txt-loss"}`}>
                  {p.qty}
                  <LotContent p={p} />
                </TableCell>
                <TableCell className={`font-mono-data text-right ${p.bad_avg_price ? "txt-loss" : ""}`}>
                  <AvgContent p={p} />
                </TableCell>
                <TableCell className={`font-mono-data text-right ${p.ltp_stale ? "txt-muted" : ""}`}
                           title={ltpTitle(p)}>
                  <LtpContent p={p} />
                </TableCell>
                <TableCell className={`font-mono-data text-right text-xs ${slHit ? "txt-loss font-bold" : "txt-muted"}`}
                           title={slTitle(p)}>
                  <SlContent p={p} />
                </TableCell>
                <TableCell className={`font-mono-data text-right text-xs ${tgtHit ? "txt-profit font-bold" : "txt-muted"}`}
                           title={tgtTitle(p)}>
                  <TargetContent p={p} />
                </TableCell>
                <TableCell className={`font-mono-data text-right ${pnlClass(p.pnl)}`}>{fmt(p.pnl)}</TableCell>
                <TableCell className={`font-mono-data text-right text-xs ${pnlClass(p.pnl)}`}>
                  {pnlPctText(p)}
                </TableCell>
                <TableCell className="font-mono-data text-xs txt-muted whitespace-nowrap" title={p.created_at || ""}>
                  {entryText(p)}
                </TableCell>
                <TableCell>
                  {closeButton(p)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
      )}
    </div>
  );
}
